import React, { useEffect } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import MapSearchBox from 'components/common/MapSearchBox';
import { GET_PROFILE, UPDATE_PROFILE } from 'graphql/user';

const ProfileDetails = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const { data } = useQuery(GET_PROFILE);
  const [mutateFunction] = useMutation(UPDATE_PROFILE, {
    refetchQueries: [GET_PROFILE]
  });

  const address = watch('address');

  const onSubmit = data => {
    mutateFunction({
      variables: {
        input: {
          firstName: data.firstName,
          lastName: data.lastName,
          introduction: data.introduction,
          addressStreet: address.street,
          addressLocality: address.locality,
          addressRegion: address.region,
          addressCountry: address.country,
          addressPostalCode: address.postalCode,
          addressFormatted: address.formatted,
          addressLat: address.lat,
          addressLng: address.lng
        }
      },
      onCompleted: () => toast.success('Profile updated')
    });
  };

  const handleLocationSelected = location => {
    setValue('address', location);
    setValue('defaultLocation', location.formatted, {
      shouldValidate: true
    });
  };

  const handleLocationCleared = () => {
    setValue('address', null);
    setValue('defaultLocation', null);
  };

  useEffect(() => {
    if (!data) {
      return;
    }
    setValue('firstName', data.profile.firstName);
    setValue('lastName', data.profile.lastName);
    setValue('introduction', data.profile.introduction);
    setValue('address', data.profile.address);
    setValue('defaultLocation', data.profile.address?.formatted);
  }, [data]);

  return (
    <Card>
      <Card.Body className="bg-body-tertiary">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3 g-3">
            <Form.Group as={Col} lg={6}>
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                {...register('firstName', {
                  required: 'First name is required.'
                })}
                isInvalid={errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} lg={6}>
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                {...register('lastName', {
                  required: 'Last name is required.'
                })}
                isInvalid={errors.lastName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Default Location</Form.Label>
            <Form.Control
              type="hidden"
              {...register('defaultLocation', {
                required: 'Default location is required.'
              })}
              isInvalid={errors.defaultLocation}
            />
            <MapSearchBox
              onSelect={handleLocationSelected}
              onClear={handleLocationCleared}
              defaultValue={address}
            />
            <Form.Control.Feedback type="invalid">
              {errors.defaultLocation?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Introduction</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              {...register('introduction')}
            />
          </Form.Group>
          <div className="text-end">
            <Button variant="primary" type="submit">
              Update
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ProfileDetails;
